// src/Admin/Login/Login.jsx — Firebase email + password sign-in
import React, { useState } from 'react';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../Firebase/firebaseConfig';
import logo from '../../Assets/logo.png';
import './Login.css';

const ERRORS = {
  'auth/invalid-credential': 'Incorrect email or password.',
  'auth/wrong-password': 'Incorrect email or password.',
  'auth/user-not-found': 'Incorrect email or password.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/too-many-requests': 'Too many attempts. Please wait a few minutes and try again.',
  'auth/network-request-failed': 'Network error. Check your connection and try again.',
  'auth/operation-not-allowed': 'Email/password sign-in is not enabled for this Firebase project yet.',
};

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    setInfo('');
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      // Admin.jsx reacts to the new auth state
    } catch (err) {
      setError(ERRORS[err.code] || 'Could not sign in. Please try again.');
      setPassword('');
    } finally {
      setBusy(false);
    }
  };

  const handleReset = async () => {
    setError('');
    setInfo('');
    if (!email.trim()) {
      setError('Enter your email above first, then click “Forgot password?”.');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email.trim());
    } catch (err) {
      if (err.code === 'auth/invalid-email') {
        setError(ERRORS['auth/invalid-email']);
        return;
      }
      // other errors are not revealed, to avoid exposing which emails exist
    }
    setInfo('If that email belongs to an admin account, a reset link has been sent.');
  };

  return (
    <div className="adm-login">
      <div className="adm-login__panel">
        <div className="adm-login__brand">
          <span className="adm-login__eyebrow">Centro Culturale Francescano</span>
          <h1>Admin<br />Console</h1>
          <p>Manage programs, publications, blog posts, enrollments and messages in one place.</p>
        </div>

        <form className="adm-login__form" onSubmit={handleSubmit} noValidate>
          <img src={logo} alt="CCF" className="adm-login__logo" />
          <h2>Sign in</h2>

          {error && <div className="adm-login__alert adm-login__alert--error" role="alert">{error}</div>}
          {info && <div className="adm-login__alert adm-login__alert--info" role="status">{info}</div>}

          <label htmlFor="adm-email">Email</label>
          <input
            id="adm-email"
            type="email"
            autoComplete="username"
            placeholder="admin@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />

          <label htmlFor="adm-password">Password</label>
          <div className="adm-login__pw">
            <input
              id="adm-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button type="button" onClick={() => setShowPassword((s) => !s)} aria-label={showPassword ? 'Hide password' : 'Show password'}>
              <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
            </button>
          </div>

          <button type="submit" className="adm-login__submit" disabled={busy || !email || !password}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
          <button type="button" className="adm-login__link" onClick={handleReset}>Forgot password?</button>
        </form>
      </div>
    </div>
  );
};

export default Login;
